import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import { ToastProvider } from './context/ToastContext.jsx';
import Layout from './components/Layout.jsx';
import { Spinner, EmptyState, Button } from './components/ui.jsx';

import Login from './pages/Login.jsx';
import Dashboard from './pages/Dashboard.jsx';

/* Everything past the dashboard loads on demand, so first paint stays small. */
const Employees = lazy(() => import('./pages/employees/Employees.jsx'));
const EmployeeDetail = lazy(() => import('./pages/employees/EmployeeDetail.jsx'));
const EmployeeForm = lazy(() => import('./pages/employees/EmployeeForm.jsx'));
const OrgChart = lazy(() => import('./pages/employees/OrgChart.jsx'));

const Attendance = lazy(() => import('./pages/attendance/Attendance.jsx'));
const Regularizations = lazy(() => import('./pages/attendance/Regularizations.jsx'));
const Resignations = lazy(() => import('./pages/employees/Resignations.jsx'));
const Invites = lazy(() => import('./pages/employees/Invites.jsx'));
const Expenses = lazy(() => import('./pages/expenses/Expenses.jsx'));
const WorkFromHome = lazy(() => import('./pages/attendance/WorkFromHome.jsx'));
const DailyTasks = lazy(() => import('./pages/attendance/DailyTasks.jsx'));
const Join = lazy(() => import('./pages/Join.jsx'));
const Devices = lazy(() => import('./pages/attendance/Devices.jsx'));

const LeaveRequests = lazy(() => import('./pages/leave/LeaveRequests.jsx'));
const LeaveBalances = lazy(() => import('./pages/leave/LeaveBalances.jsx'));
const Holidays = lazy(() => import('./pages/leave/Holidays.jsx'));

const PayrollRuns = lazy(() => import('./pages/payroll/PayrollRuns.jsx'));
const PayrollRunDetail = lazy(() => import('./pages/payroll/PayrollRunDetail.jsx'));
const SalaryStructures = lazy(() => import('./pages/payroll/SalaryStructures.jsx'));
const Payslips = lazy(() => import('./pages/payroll/Payslips.jsx'));

const Documents = lazy(() => import('./pages/documents/Documents.jsx'));
const Form16 = lazy(() => import('./pages/documents/Form16.jsx'));

const Reports = lazy(() => import('./pages/Reports.jsx'));
const Settings = lazy(() => import('./pages/Settings.jsx'));
const AuditLog = lazy(() => import('./pages/AuditLog.jsx'));
const Notifications = lazy(() => import('./pages/Notifications.jsx'));
const Profile = lazy(() => import('./pages/Profile.jsx'));

/** Blocks a route until the session is known; remembers where to return to. */
function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <Spinner label="Signing you in…" />;
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

/** Route-level permission gate, so a hand-typed URL cannot bypass the sidebar. */
function RequirePermission({ permission, anyOf, children }) {
  const { can, canAny } = useAuth();
  const allowed = permission ? can(permission) : anyOf ? canAny(...anyOf) : true;

  if (!allowed) {
    return (
      <EmptyState
        icon="🔒"
        title="You do not have access to this page"
        message="Ask an administrator if you believe you should."
        action={<Button variant="secondary" onClick={() => window.history.back()}>Go back</Button>}
      />
    );
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Self-registration from an invitation link. Public by design:
                whoever opens it has no account yet. */}
            <Route
              path="/join/:token"
              element={
                <Suspense fallback={<Spinner />}>
                  <Join />
                </Suspense>
              }
            />

            <Route
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route
                path="/"
                element={
                  <Suspense fallback={<Spinner />}>
                    <Dashboard />
                  </Suspense>
                }
              />

              {/* People */}
              <Route path="/employees" element={<Guard anyOf={['employee.view.all', 'employee.view.team']}><Employees /></Guard>} />
              <Route path="/employees/new" element={<Guard permission="employee.create"><EmployeeForm /></Guard>} />
              <Route path="/employees/:id" element={<Guard><EmployeeDetail /></Guard>} />
              <Route path="/employees/:id/edit" element={<Guard permission="employee.update"><EmployeeForm /></Guard>} />
              <Route path="/org-chart" element={<Guard><OrgChart /></Guard>} />

              {/* Attendance */}
              <Route path="/attendance" element={<Guard anyOf={['attendance.view.all', 'attendance.view.team']}><Attendance /></Guard>} />
              <Route path="/employees/invites" element={<Guard permission="employee.invite"><Invites /></Guard>} />
              <Route path="/expenses" element={<Guard anyOf={['expense.view.all', 'expense.view.team']}><Expenses /></Guard>} />
              <Route path="/resignations" element={<Guard anyOf={['resignation.view.team', 'resignation.view.all']}><Resignations /></Guard>} />
              <Route path="/attendance/regularizations" element={<Guard anyOf={['attendance.regularize.approve', 'attendance.view.all']}><Regularizations /></Guard>} />
              <Route path="/attendance/wfh" element={<Guard anyOf={['wfh.view.all', 'wfh.view.team']}><WorkFromHome /></Guard>} />
              <Route path="/attendance/tasks" element={<Guard anyOf={['task.view.all', 'task.view.team']}><DailyTasks /></Guard>} />
              <Route path="/attendance/devices" element={<Guard permission="attendance.device.manage"><Devices /></Guard>} />

              {/* Leave */}
              <Route path="/leave" element={<Guard anyOf={['leave.view.all', 'leave.view.team']}><LeaveRequests /></Guard>} />
              <Route path="/leave/balances" element={<Guard permission="leave.view.all"><LeaveBalances /></Guard>} />
              <Route path="/holidays" element={<Guard><Holidays /></Guard>} />

              {/* Payroll */}
              <Route path="/payroll" element={<Guard permission="payroll.view.all"><PayrollRuns /></Guard>} />
              <Route path="/payroll/runs/:id" element={<Guard permission="payroll.view.all"><PayrollRunDetail /></Guard>} />
              <Route path="/payroll/structures" element={<Guard permission="payroll.structure.manage"><SalaryStructures /></Guard>} />
              <Route path="/payroll/payslips" element={<Guard permission="payroll.view.all"><Payslips /></Guard>} />

              {/* Documents */}
              <Route path="/documents" element={<Guard anyOf={['document.manage', 'document.view.all']}><Documents /></Guard>} />
              <Route path="/documents/form16" element={<Guard permission="document.manage"><Form16 /></Guard>} />

              {/* Admin */}
              <Route path="/reports" element={<Guard permission="report.view"><Reports /></Guard>} />
              <Route path="/settings" element={<Guard permission="settings.manage"><Settings /></Guard>} />
              <Route path="/audit" element={<Guard permission="audit.view"><AuditLog /></Guard>} />

              <Route path="/notifications" element={<Guard><Notifications /></Guard>} />
              <Route path="/profile" element={<Guard><Profile /></Guard>} />

              <Route
                path="*"
                element={
                  <EmptyState
                    icon="🧭"
                    title="Page not found"
                    message="That page does not exist."
                    action={<Button onClick={() => window.location.assign('/')}>Back to dashboard</Button>}
                  />
                }
              />
            </Route>
          </Routes>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

/** Suspense + permission gate in one wrapper, to keep the route table readable. */
function Guard({ permission, anyOf, children }) {
  return (
    <RequirePermission permission={permission} anyOf={anyOf}>
      <Suspense fallback={<Spinner />}>{children}</Suspense>
    </RequirePermission>
  );
}
